/**
 * Translations Management Page
 * Admin-sida för att hantera översättningar
 * Översättningar lagras i filer (sv.ts och en.ts)
 */

import { useEffect, useState } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, LanguageIcon } from '@heroicons/react/24/outline';
import { translationsSv } from '@/i18n/locales/sv';
import { translationsEn } from '@/i18n/locales/en';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';

interface TranslationItem {
  key: string;
  sv: string;
  en: string;
  namespace: string;
}

const TranslationsManagement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [translations, setTranslations] = useState<TranslationItem[]>([]);
  const [filteredTranslations, setFilteredTranslations] = useState<TranslationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ sv: string; en: string }>({ sv: '', en: '' });

  useEffect(() => {
    loadTranslations();
  }, []);

  useEffect(() => {
    // Filtrera översättningar baserat på sökterm
    if (!searchTerm.trim()) {
      setFilteredTranslations(translations);
    } else {
      const filtered = translations.filter(
        (trans) =>
          trans.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
          trans.sv.toLowerCase().includes(searchTerm.toLowerCase()) ||
          trans.en.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTranslations(filtered);
    }
  }, [searchTerm, translations]);

  /**
   * Ladda översättningar från filer och konvertera till flat struktur
   */
  const loadTranslations = () => {
    try {
      setLoading(true);
      const flatTranslations: TranslationItem[] = [];

      // Konvertera nested objekt till flat struktur
      const flatten = (obj: any, prefix = '', namespace = 'common'): void => {
        Object.keys(obj).forEach((key) => {
          const value = obj[key];
          const fullKey = prefix ? `${prefix}.${key}` : key;

          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // Detta är en namespace, fortsätt rekursivt
            const newNamespace = prefix ? prefix : key;
            flatten(value, fullKey, newNamespace);
          } else if (typeof value === 'string') {
            // Detta är en översättning - hämta från båda filerna
            const keys = fullKey.split('.');
            let svValue: any = translationsSv;
            let enValue: any = translationsEn;
            
            // Navigera genom nested struktur
            for (const k of keys) {
              svValue = svValue?.[k];
              enValue = enValue?.[k];
            }

            flatTranslations.push({
              key: fullKey,
              sv: typeof svValue === 'string' ? svValue : '',
              en: typeof enValue === 'string' ? enValue : '',
              namespace: namespace,
            });
          }
        });
      };

      // Flatten båda språken
      flatten(translationsSv);
      flatten(translationsEn);

      // Ta bort duplicerade keys och kombinera sv/en
      const uniqueTranslations = new Map<string, TranslationItem>();
      flatTranslations.forEach((trans) => {
        if (!uniqueTranslations.has(trans.key)) {
          uniqueTranslations.set(trans.key, trans);
        } else {
          const existing = uniqueTranslations.get(trans.key)!;
          if (trans.sv && !existing.sv) existing.sv = trans.sv;
          if (trans.en && !existing.en) existing.en = trans.en;
        }
      });

      setTranslations(Array.from(uniqueTranslations.values()));
      setFilteredTranslations(Array.from(uniqueTranslations.values()));
    } catch (error: any) {
      toast.error(t('translations.loadError') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (translation: TranslationItem) => {
    setEditingKey(translation.key);
    setEditForm({ sv: translation.sv, en: translation.en });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditForm({ sv: '', en: '' });
  };

  const saveTranslation = async (key: string) => {
    try {
      // I en riktig implementation skulle vi skriva till filerna här
      // Men eftersom vi är i webbläsaren kan vi inte skriva till filsystemet direkt
      // Detta kräver en backend-endpoint eller en build-time process
      
      toast.success(t('translations.updatedNote'));
      setEditingKey(null);
      
      // Uppdatera lokal state
      setTranslations((prev) =>
        prev.map((trans) =>
          trans.key === key
            ? { ...trans, sv: editForm.sv, en: editForm.en }
            : trans
        )
      );
    } catch (error: any) {
      toast.error(t('translations.saveError') + ': ' + error.message);
    }
  };

  const exportToFiles = () => {
    // Generera TypeScript-kod för filerna
    const svCode = generateTypeScriptFile(translations, 'sv');
    const enCode = generateTypeScriptFile(translations, 'en');
    
    // Skapa blob och ladda ner
    const svBlob = new Blob([svCode], { type: 'text/typescript' });
    const enBlob = new Blob([enCode], { type: 'text/typescript' });
    
    const svUrl = URL.createObjectURL(svBlob);
    const enUrl = URL.createObjectURL(enBlob);
    
    const svLink = document.createElement('a');
    svLink.href = svUrl;
    svLink.download = 'sv.ts';
    svLink.click();
    
    const enLink = document.createElement('a');
    enLink.href = enUrl;
    enLink.download = 'en.ts';
    enLink.click();
    
    URL.revokeObjectURL(svUrl);
    URL.revokeObjectURL(enUrl);
    
    toast.success(t('translations.exportFiles') + '! Kopiera innehållet till rätt filer.');
  };

  const generateTypeScriptFile = (translations: TranslationItem[], lang: 'sv' | 'en'): string => {
    const grouped: Record<string, Record<string, string>> = {};
    
    translations.forEach((trans) => {
      const keys = trans.key.split('.');
      const namespace = keys[0];
      const key = keys.slice(1).join('.');
      
      if (!grouped[namespace]) {
        grouped[namespace] = {};
      }
      
      grouped[namespace][key] = lang === 'sv' ? trans.sv : trans.en;
    });
    
    let code = `/**\n * ${lang === 'sv' ? 'Swedish' : 'English'} Translations\n */\n\n`;
    code += `export const translations${lang === 'sv' ? 'Sv' : 'En'}: Record<string, any> = {\n`;
    
    Object.keys(grouped).forEach((namespace) => {
      code += `  // ${namespace}\n`;
      code += `  ${namespace}: {\n`;
      Object.keys(grouped[namespace]).forEach((key) => {
        const value = grouped[namespace][key];
        code += `    ${key}: '${value.replace(/'/g, "\\'")}',\n`;
      });
      code += `  },\n\n`;
    });
    
    code += `};\n`;
    
    return code;
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="text-textTertiary">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <LanguageIcon className="w-6 h-6 text-primary" />
            {t('translations.title')}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={exportToFiles}
            className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('translations.exportFiles')}
          </button>
          <input
            type="text"
            placeholder={t('translations.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
          />
        </div>
      </div>

      <div className="mb-4 p-4 bg-yellow-100 dark:bg-yellow-900 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
        <strong>{t('translations.exportNoteLabel')}:</strong> {t('translations.exportNote')}
      </div>

      {/* Översättningstabell */}
      <div className="bg-bgElevated rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bgLight border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-textDefault">
                  {t('translations.key')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-textDefault">
                  {t('translations.swedish')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-textDefault">
                  {t('translations.english')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-textDefault">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredTranslations.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-textSecondary">
                    {searchTerm
                      ? t('translations.noMatchSearch')
                      : t('translations.noTranslations')}
                  </td>
                </tr>
              ) : (
                filteredTranslations.map((translation) => (
                  <tr key={translation.key} className="hover:bg-bgLight transition-colors">
                    <td className="px-4 py-3 text-sm font-mono text-textSecondary">
                      {translation.key}
                    </td>
                    <td className="px-4 py-3">
                      {editingKey === translation.key ? (
                        <input
                          type="text"
                          value={editForm.sv}
                          onChange={(e) => setEditForm({ ...editForm, sv: e.target.value })}
                          className="w-full px-2 py-1 border border-border rounded bg-bgContainer text-textDefault"
                        />
                      ) : (
                        <span className="text-sm text-textDefault">{translation.sv}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingKey === translation.key ? (
                        <input
                          type="text"
                          value={editForm.en}
                          onChange={(e) => setEditForm({ ...editForm, en: e.target.value })}
                          className="w-full px-2 py-1 border border-border rounded bg-bgContainer text-textDefault"
                        />
                      ) : (
                        <span className="text-sm text-textDefault">{translation.en}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {editingKey === translation.key ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => saveTranslation(translation.key)}
                            className="px-3 py-1 text-sm bg-bgPrimary text-textLight rounded hover:opacity-90 transition-opacity"
                          >
                            {t('common.save')}
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="px-3 py-1 text-sm bg-bgLight text-textDefault rounded hover:bg-bgElevated transition-colors"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(translation)}
                          className="px-3 py-1 text-sm bg-bgLight text-textDefault rounded hover:bg-bgPrimary hover:text-textLight transition-colors"
                        >
                          {t('common.edit')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-sm text-textSecondary">
        {t('translations.total')}: {filteredTranslations.length} {t('translations.title').toLowerCase()}
        {searchTerm && ` (${t('translations.filtered')} ${translations.length})`}
      </div>
    </div>
  );
};

export default TranslationsManagement;
